
import { gql, ApolloClient, InMemoryCache } from '@apollo/client';
import { parseAbi, encodeFunctionData } from 'viem';

// Updated to QuickSwap V3 official endpoint
const QUICKSWAP_API = "https://api.studio.thegraph.com/query/48211/quickswap-v3-polygon/version/latest";

// QuickSwap router address on Polygon
const QUICKSWAP_ROUTER = '0xa5E0829CaCEd8fFDD4De3c43696c57F7D7A678ff';

// WMATIC address on Polygon
const WMATIC_ADDRESS = '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270';

// Router ABI for the functions we need
const ROUTER_ABI = parseAbi([
  'function addLiquidityETH(address token, uint amountTokenDesired, uint amountTokenMin, uint amountETHMin, address to, uint deadline) payable returns (uint amountToken, uint amountETH, uint liquidity)',
  'function removeLiquidityETH(address token, uint liquidity, uint amountTokenMin, uint amountETHMin, address to, uint deadline) returns (uint amountToken, uint amountETH)'
]);

// Create an Apollo Client instance
const client = new ApolloClient({
  uri: QUICKSWAP_API,
  cache: new InMemoryCache()
});

// GraphQL query to fetch top pools by volume
const TOP_POOLS_QUERY = gql`
  query getTopPools($limit: Int!) {
    pools(
      first: $limit, 
      orderBy: volumeUSD, 
      orderDirection: desc, 
      where: { totalValueLockedUSD_gt: "10000" }
    ) {
      id
      token0 {
        symbol
        name
      }
      token1 {
        symbol
        name
      }
      totalValueLockedUSD
      volumeUSD
    }
  }
`;

/**
 * Fetches the top QuickSwap pools sorted by volume
 * @param limit Number of pools to fetch (default: 3)
 * @returns Promise resolving to pools data
 */
export async function fetchTopPools(limit = 3) {
  try {
    const response = await client.query({
      query: TOP_POOLS_QUERY,
      variables: { limit },
      fetchPolicy: 'network-only', // Don't use cache for this query
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching QuickSwap pools:', error);
    // Return fallback data immediately on error
    console.log("Using fallback pool data");
    return fallbackPoolsData;
  }
}

/**
 * Adds liquidity to a token pair and then burns the LP tokens to create permanent liquidity
 * @param walletClient The wallet client to use for the transaction
 * @param tokenAddress The address of the token
 * @param tokenAmount The amount of tokens to add
 * @param liquidityAmount The amount of MATIC to add as liquidity
 * @returns The transaction hash
 */
export async function addAndBurnLiquidity(
  walletClient: any,
  tokenAddress: `0x${string}`,
  tokenAmount: bigint,
  liquidityAmount: bigint
) {
  try {
    console.log(`Adding liquidity for token ${tokenAddress}`);
    console.log(`Token amount: ${tokenAmount}, MATIC amount: ${liquidityAmount}`);
    
    // Deadline: 20 minutes from now
    const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200);
    
    // Calculate minimum amounts (with 2% slippage tolerance)
    const minTokenAmount = tokenAmount * BigInt(98) / BigInt(100);
    const minMaticAmount = liquidityAmount * BigInt(98) / BigInt(100);
    
    // Set up zero address for burning (sending LP tokens to this address effectively burns them)
    const DEAD_ADDRESS = "0x000000000000000000000000000000000000dEaD";
    
    // Encode function data for addLiquidityETH
    const data = encodeFunctionData({
      abi: ROUTER_ABI,
      functionName: 'addLiquidityETH',
      args: [
        tokenAddress,
        tokenAmount,
        minTokenAmount,
        minMaticAmount,
        DEAD_ADDRESS, // LP tokens go to burn address
        deadline
      ]
    });
    
    // Send transaction
    const hash = await walletClient.sendTransaction({
      to: QUICKSWAP_ROUTER,
      data,
      value: liquidityAmount,
    });
    
    console.log(`Liquidity added and burned successfully. Transaction: ${hash}`);
    return hash;
  } catch (error) {
    console.error('Error in addAndBurnLiquidity:', error);
    throw new Error(`Failed to add and burn liquidity: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Fallback data in case the API is unavailable
export const fallbackPoolsData = {
  pools: [
    {
      id: "1",
      token0: { symbol: "MATIC", name: "Polygon" },
      token1: { symbol: "USDC", name: "USD Coin" },
      totalValueLockedUSD: "1234567",
      volumeUSD: "987654"
    },
    {
      id: "2",
      token0: { symbol: "ETH", name: "Ethereum" },
      token1: { symbol: "USDT", name: "Tether" },
      totalValueLockedUSD: "2345678",
      volumeUSD: "876543"
    },
    {
      id: "3",
      token0: { symbol: "QUICK", name: "QuickSwap" },
      token1: { symbol: "MATIC", name: "Polygon" },
      totalValueLockedUSD: "3456789",
      volumeUSD: "765432"
    }
  ]
};
